import React from 'react';
import { Tag } from '@decisiv/ui-components';

const STATUS_INTENT = { ok: 'success', warn: 'warning', exceeded: 'danger' };
const STATUS_LABEL = { ok: 'OK', warn: 'Warn', exceeded: 'Exceeded' };

const renderStatus = (record) => (
  <Tag
    intent={STATUS_INTENT[record.status] ?? 'neutral'}
    text={STATUS_LABEL[record.status] ?? record.status}
  />
);

const formatUsd = (value) =>
  value == null ? '—' : `$${Number(value).toFixed(2)}`;

const formatTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return date.toLocaleTimeString();
};

const aiDealerBudgetConfig = {
  name: 'Dealer Budget',
  endpoint: '/internal_admin/admin_kit/ai_dealer_budgets',
  // Must match Portal::AiDealerBudget.model_name.param_key, which Rails
  // derives from the full namespaced class name (portal_ai_dealer_budget,
  // not ai_dealer_budget) — createAdminResource's generated create/update
  // wrap the form body under this key, and BaseController#permitted_params
  // requires that same key on the way in.
  modelKey: 'portal_ai_dealer_budget',
  titleField: 'dealer_label',

  columns: [
    { name: 'dealer_label', title: 'Dealer', type: 'link' },
    {
      name: 'daily_warn_usd',
      title: 'Warn ($/day)',
      render: (r) => formatUsd(r.daily_warn_usd),
    },
    {
      name: 'daily_limit_usd',
      title: 'Limit ($/day)',
      render: (r) => formatUsd(r.daily_limit_usd),
    },
    {
      name: 'today_spend_usd',
      title: 'Today',
      render: (r) => formatUsd(r.today_spend_usd),
    },
    { name: 'status', title: 'Status', render: renderStatus },
    {
      name: 'exceeded_at',
      title: 'Exceeded At',
      render: (r) =>
        r.status === 'exceeded' ? formatTime(r.exceeded_at) : '—',
    },
    { name: 'enabled', title: 'Enabled', type: 'boolean' },
  ],

  attributes: [
    { name: 'dealer_label', label: 'Dealer' },
    {
      name: 'daily_warn_usd',
      label: 'Warn threshold ($/day)',
      render: (r) => formatUsd(r.daily_warn_usd),
    },
    {
      name: 'daily_limit_usd',
      label: 'Limit ($/day)',
      render: (r) => formatUsd(r.daily_limit_usd),
    },
    {
      name: 'today_spend_usd',
      label: "Today's spend",
      render: (r) => formatUsd(r.today_spend_usd),
    },
    { name: 'status', label: 'Status', render: renderStatus },
    {
      name: 'exceeded_at',
      label: 'Exceeded At (Today)',
      render: (r) =>
        r.status === 'exceeded' ? formatTime(r.exceeded_at) : '—',
    },
    {
      name: 'warn_at',
      label: 'Warn At (Today)',
      render: (r) =>
        r.status === 'warn' || r.status === 'exceeded'
          ? formatTime(r.warn_at)
          : '—',
    },
    { name: 'enabled', label: 'Enabled', type: 'boolean' },
    { name: 'notes', label: 'Notes' },
    {
      name: 'exceeded_history_link',
      label: 'History',
      render: (r) => (
        <a
          href={`/internal_admin/admin_kit/ai_dealer_budgets/exceeded_history/${r.dealer_id}`}
        >
          View history
        </a>
      ),
    },
    { name: 'created_at', label: 'Created At' },
    { name: 'updated_at', label: 'Updated At' },
  ],

  form: {
    fields: [
      {
        name: 'dealer_id',
        label: 'Dealer',
        type: 'typeahead',
        searchEndpoint:
          '/internal_admin/admin_kit/ai_dealer_budgets/dealer_search',
        displayField: 'dealer_label',
        helpMessage: 'Search by dealer name or Decisiv ID (min 2 characters)',
      },
      {
        name: 'daily_warn_usd',
        label: 'Warn threshold ($/day)',
        type: 'text',
        helpMessage: 'Leave blank to inherit the system default',
      },
      {
        name: 'daily_limit_usd',
        label: 'Limit ($/day)',
        type: 'text',
        helpMessage: 'Leave blank to inherit the system default',
      },
      { name: 'notes', label: 'Notes', type: 'textarea', rows: 3, span: 2 },
      {
        name: 'enabled',
        label: 'Enabled',
        type: 'checkbox',
        defaultValue: true,
      },
    ],
  },
};

export default aiDealerBudgetConfig;
